type DatadogRum = typeof import("@datadog/browser-rum")["datadogRum"];
type DatadogLogs = typeof import("@datadog/browser-logs")["datadogLogs"];

let initializedPromise: Promise<void> | null = null;
let rum: DatadogRum | null = null;
let logs: DatadogLogs | null = null;

// SDK 7 は IE 非対応なので読み込まない
const isUnsupportedBrowser = () => {
  const ua = window.navigator.userAgent.toLowerCase();
  return ua.indexOf("msie") != -1 || ua.indexOf("trident") != -1;
};

// main バンドルがすでに 3MB を超えているため、静的 import せず別チャンクに切り出す
const loadSdk = () =>
  Promise.all([
    import("@datadog/browser-rum"),
    import("@datadog/browser-logs")
  ]);

export const initDatadog = (): Promise<void> => {
  if (typeof window === "undefined" || isUnsupportedBrowser()) {
    return Promise.resolve();
  }

  if (!DATADOG_APPLICATION_ID || !DATADOG_CLIENT_TOKEN) {
    return Promise.resolve();
  }

  if (!initializedPromise) {
    initializedPromise = loadSdk()
      .then(([{ datadogRum }, { datadogLogs }]) => {
        rum = datadogRum;
        logs = datadogLogs;

        const common = {
          clientToken: DATADOG_CLIENT_TOKEN,
          site: DATADOG_SITE,
          service: DATADOG_SERVICE,
          env: DATADOG_ENV,
          version: VERSION_HASH
        };

        const isHeadless =
          /HeadlessChrome/i.test(navigator.userAgent) ||
          navigator.webdriver === true;

        datadogRum.init({
          ...common,
          applicationId: DATADOG_APPLICATION_ID,
          sessionSampleRate: isHeadless ? 0 : 100,
          sessionReplaySampleRate: 100,
          trackUserInteractions: true,
          trackResources: true,
          trackLongTasks: true,
          defaultPrivacyLevel: "mask-user-input"
        });

        datadogLogs.init({
          ...common,
          forwardErrorsToLogs: true,
          sessionSampleRate: 100
        });
      })
      .catch(error => {
        initializedPromise = null;
        rum = null;
        logs = null;

        if (IS_DEBUG) {
          console.error(error);
        }
      });
  }

  return initializedPromise;
};

// テスト用: モジュールレベルのキャッシュをリセットする
export const __resetDatadogForTest = (): void => {
  initializedPromise = null;
  rum = null;
  logs = null;
};

// userId が空のときはログアウトなので紐づけを解除する
export const setDatadogUser = (userId: string): Promise<void> =>
  initializedPromise
    ? initializedPromise.then(() => {
        if (!rum || !logs) {
          return;
        }

        if (userId) {
          rum.setUser({ id: userId });
          logs.setUser({ id: userId });
        } else {
          rum.clearUser();
          logs.clearUser();
        }
      })
    : Promise.resolve();

// 未初期化 (Datadog 無効の環境も含む) のときは送らない
export const addDatadogError = (error: unknown): Promise<void> =>
  initializedPromise
    ? initializedPromise.then(() => {
        if (rum) {
          rum.addError(error);
        }
      })
    : Promise.resolve();
